import { useCallback, useEffect, useState } from 'react'
import { BOX_TRUCK_COLUMN_DEFAULT_VISIBLE, BOX_TRUCK_COLUMN_IDS, type BoxTruckColumnId } from '../components/BoxTruckTable'

const storageKeyFor = (userId: string | undefined): string => `boxTruckLoads:columns:${userId ?? 'anon'}`

/**
 * Bump when the DEFAULT set changes meaningfully, to retire layouts saved against the old one.
 *
 * Reconciling key-by-key (below) is right for a column being added or removed, but it cannot express "the
 * default itself moved": a layout saved while Age was on keeps Age on forever, because a stored `true` is
 * indistinguishable from a deliberate choice. The version is how that distinction gets made — same reason
 * the DS ProfileTable versions its own layout snapshot.
 *
 * 2 — Posted became the default read and Age moved into the opt-in set.
 */
const LAYOUT_VERSION = 2

interface PersistedLayout {
    version: number
    columns: Partial<Record<BoxTruckColumnId, boolean>>
    order?: BoxTruckColumnId[]
}

interface BoxTruckColumnLayout {
    visibleColumns: Record<BoxTruckColumnId, boolean>
    columnOrder: BoxTruckColumnId[]
}

/** A fresh copy of the first-run layout — callers mutate their copy while reconciling a stored one. */
const defaultVisibleColumns = (): Record<BoxTruckColumnId, boolean> => ({ ...BOX_TRUCK_COLUMN_DEFAULT_VISIBLE })
const defaultColumnOrder = (): BoxTruckColumnId[] => [...BOX_TRUCK_COLUMN_IDS]
const isBoxTruckColumnId = (value: unknown): value is BoxTruckColumnId => typeof value === 'string' && BOX_TRUCK_COLUMN_IDS.some((id) => id === value)

/** Keep each current column exactly once, preserving any valid saved positions and appending new columns. */
const normalizeColumnOrder = (value: unknown): BoxTruckColumnId[] => {
    if (!Array.isArray(value)) return defaultColumnOrder()

    const knownIds = new Set<BoxTruckColumnId>(BOX_TRUCK_COLUMN_IDS)
    const normalized: BoxTruckColumnId[] = []
    for (const id of value) {
        if (isBoxTruckColumnId(id) && knownIds.has(id) && !normalized.includes(id)) {
            normalized.push(id)
        }
    }
    for (const id of BOX_TRUCK_COLUMN_IDS) {
        if (!normalized.includes(id)) normalized.push(id)
    }
    return normalized
}

/**
 * Restore, then reconcile against the CURRENT column list rather than trusting the stored shape.
 *
 * A saved layout outlives the code that wrote it: a column added later would be missing from the entry and
 * a removed one would linger. Reading it key-by-key means a new column arrives at its own default (visible
 * unless it is opt-in) instead of vanishing because an old entry never mentioned it, and a dropped column
 * is ignored. A stale VERSION is discarded outright — see LAYOUT_VERSION. A malformed entry degrades to
 * the defaults rather than blanking the board.
 */
const readLayout = (storageKey: string): BoxTruckColumnLayout => {
    const defaults = defaultVisibleColumns()
    try {
        const raw = localStorage.getItem(storageKey)
        if (!raw) return { visibleColumns: defaults, columnOrder: defaultColumnOrder() }
        const saved = JSON.parse(raw) as Partial<PersistedLayout>
        // An older version (or the pre-versioning shape, which carries no `version` at all) was saved
        // against defaults that no longer exist — take the current ones rather than resurrecting the old
        // board.
        if (!saved || typeof saved !== 'object' || saved.version !== LAYOUT_VERSION) {
            return { visibleColumns: defaults, columnOrder: defaultColumnOrder() }
        }
        const columns = saved.columns
        if (!columns || typeof columns !== 'object') return { visibleColumns: defaults, columnOrder: defaultColumnOrder() }
        for (const id of BOX_TRUCK_COLUMN_IDS) {
            if (typeof columns[id] === 'boolean') defaults[id] = columns[id] as boolean
        }
        return { visibleColumns: defaults, columnOrder: normalizeColumnOrder(saved.order) }
    } catch {
        return { visibleColumns: defaults, columnOrder: defaultColumnOrder() }
    }
}

/**
 * The operator's column choice for the box-truck board, persisted per user.
 *
 * Per USER, not per window: unlike the search filters (which are a per-window working set), which columns
 * you read a load by is a preference — it should follow you into a new tab, not reset there.
 */
export const useBoxTruckColumns = (userId: string | undefined) => {
    const storageKey = storageKeyFor(userId)
    const [layout, setLayout] = useState<BoxTruckColumnLayout>(() => readLayout(storageKey))

    // The hook mounts before auth resolves, so the first read is the anonymous key; re-read once the real
    // user id arrives rather than leaving them on defaults until a manual change.
    useEffect(() => setLayout(readLayout(storageKey)), [storageKey])

    const persist = useCallback(
        (next: BoxTruckColumnLayout) => {
            setLayout(next)
            try {
                localStorage.setItem(
                    storageKey,
                    JSON.stringify({ version: LAYOUT_VERSION, columns: next.visibleColumns, order: next.columnOrder } satisfies PersistedLayout)
                )
            } catch {
                // A full or blocked storage must never cost the operator the change they just made.
            }
        },
        [storageKey]
    )

    const toggleColumn = useCallback(
        (id: BoxTruckColumnId) => persist({ ...layout, visibleColumns: { ...layout.visibleColumns, [id]: !layout.visibleColumns[id] } }),
        [layout, persist]
    )

    const reorderColumns = useCallback(
        (visibleOrder: BoxTruckColumnId[]) => {
            const visibleIds = new Set(BOX_TRUCK_COLUMN_IDS.filter((id) => layout.visibleColumns[id]))
            const nextVisible = normalizeColumnOrder(visibleOrder).filter((id) => visibleIds.has(id))
            const hidden = layout.columnOrder.filter((id) => !visibleIds.has(id))
            persist({ ...layout, columnOrder: [...nextVisible, ...hidden] })
        },
        [layout, persist]
    )

    const resetColumns = useCallback(() => persist({ visibleColumns: defaultVisibleColumns(), columnOrder: defaultColumnOrder() }), [persist])

    return { visibleColumns: layout.visibleColumns, columnOrder: layout.columnOrder, toggleColumn, reorderColumns, resetColumns }
}
